import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN, Array } from '@blade/std-lib'
import { InfoMessage } from '@root/shared/ui-kit'

import { recommendationsModel } from '../atoms/reatorm-who-to-follow'

import { WhoToFollowItem } from './who-to-follow-item'

import type { DelugeId } from '@root/entities/user-short'
import type { ClassNameProps } from '@root/shared/react'

type WhoToFollowCardProps = {
  delugeId: DelugeId
} & ClassNameProps

export const WhoToFollowList = (props: WhoToFollowCardProps) => {
  const { t } = useTranslation()
  const { recommendationsStateAtom } = useMemo(
    () => recommendationsModel({ delugeId: props.delugeId }),
    [props.delugeId],
  )
  const [whoToFollowState] = useAtom(recommendationsStateAtom)
  const refresh = useAction(recommendationsStateAtom.refresh)

  return (
    <Root className={props.className}>
      <Header>{t('common.who_to_follow')}</Header>

      {AsyncStateN.matchWaiting(whoToFollowState, {
        Success: (followerDetails) => {
          if (followerDetails.length <= 0) {
            return (
              <InfoContainer>
                <InfoMessage
                  intent="empty"
                  variant="dt"
                  description={t('common.no-recommendations')}
                />
              </InfoContainer>
            )
          }
          return (
            <Body>
              {followerDetails.map((fd) => (
                <WhoToFollowItem key={fd.delugeId} followerDetail={fd} />
              ))}
            </Body>
          )
        },
        Error: () => (
          <InfoContainer>
            <InfoMessage intent={'error'} variant="dt" onRetry={refresh} />
          </InfoContainer>
        ),
        Waiting: () => (
          <Body>
            {Array.range(0, 5).map((i) => (
              <WhoToFollowItem.Skeleton key={i} />
            ))}
          </Body>
        ),
      })}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  overflow: hidden;
  grid-template-rows: auto minmax(0, 1fr);
  border-radius: 10px;
  background: var(--appearanceA-3);
`

const Header = styled.div`
  text-align: center;
  padding: 10px 12px;
  color: var(--text-12);

  font-size: 12px;
  font-weight: 600;
`

const Body = styled.div`
  padding: 6px 12px 12px;
  display: grid;
  gap: 6px;
  grid-auto-rows: max-content;
  overflow: auto;
`

const InfoContainer = styled.div`
  display: grid;
  padding: 12px;
`
